/**
 * Whether an edit changed what the roster holds — the test for the "Saved" toast.
 *
 * A counter row with no counter yet is never sent to the server, so adding one, or editing its
 * times or note, saves nothing and must not announce it. Row ids and order are not roster state.
 */

interface RowLike {
  startTime: string;
  endTime: string;
  counter: string;
  notes?: string;
  duty?: string;
}

/** A counter row, or a free-text duty row (counter "0"): both are held by the server. */
const hasCounter = (r: { counter: string; duty?: string }) => !!r.duty || (r.counter !== '' && r.counter !== '0');

/** A canonical form of the rows the server would hold: counter rows only, order-independent. */
export function heldRows(rows: RowLike[]): string {
  return JSON.stringify(
    rows.filter(hasCounter).map((r) => [r.startTime, r.endTime, r.counter, r.notes ?? '', r.duty ?? '']).sort(),
  );
}

export function rowsChangedRoster(before: RowLike[], after: RowLike[]): boolean {
  return heldRows(before) !== heldRows(after);
}

/** Availability windows differ (same windows in another order are the same). */
export function windowsChanged(
  before: Array<{ start: string; end: string }>,
  after: Array<{ start: string; end: string }>,
): boolean {
  const key = (w: Array<{ start: string; end: string }>) =>
    JSON.stringify(w.map((s) => [s.start, s.end]).sort());
  return key(before) !== key(after);
}

/**
 * Whether an availability write is worth a "Saved" toast: the windows really changed, and the user
 * asked for it — a window only stretched to hold a counter-less row (`silent`) is a side effect.
 */
export function availabilityNews(
  before: Array<{ start: string; end: string }>,
  after: Array<{ start: string; end: string }>,
  silent: boolean,
): boolean {
  return !silent && windowsChanged(before, after);
}
